// Credit Card Service for SwipeSage Admin Panel
import { firestoreService } from '../firestore';
import { CreditCard } from '@shared/firebase/schema';
import { mitcService, MitcRecord } from './mitcService';
import { auditService, computeDiff } from './auditService';
import { ReferralService, ApplyLinkResult } from '@shared/services/referralService';
import { authService } from '../auth';
import { deleteField, type UpdateData } from 'firebase/firestore';
import { logger } from '../logger';

export interface CreditCardFilters {
  category?: string;
  bank?: string;
  isActive?: boolean;
  isFeatured?: boolean;
}

export interface CreateCreditCardData {
  name: string;
  bank: string;
  category: string;
  cardType: 'credit' | 'debit' | 'prepaid';
  description: string;
  features: string[];
  eligibility: string[];
  imageUrl: string;
  logoUrl: string;
  annualFee: number;
  joiningFee: number;
  interestRate: number;
  creditLimit: {
    min: number;
    max: number;
  };
  rewards: {
    cashbackRate: number;
    rewardPoints: number;
    welcomeBonus: number;
    categories: Array<{
      category: string;
      rate: number;
      description: string;
      maxRewards?: number;
    }>;
  };
  fees: {
    annualFee: number;
    joiningFee: number;
    latePaymentFee: number;
    cashAdvanceFee: number;
    foreignTransactionFee: number;
  };
  application: {
    minIncome: number;
    minAge: number;
    maxAge: number;
    employmentType: string[];
    creditScore: {
      min: number;
      max: number;
    };
    documents: string[];
    processingTime: string;
    approvalRate: number;
  };
  isActive?: boolean;
  isFeatured?: boolean;
  tags?: string[];
}

class CreditCardService {
  // Get all credit cards including MITC records, merged.
  // Existing card.mitc data (especially referralLink) takes priority over
  // freshly-converted MITC record data — this preserves admin edits.
  async getAllCards(filters: CreditCardFilters = {}, pageSize: number = 1000) {
    try {
      const [creditCardsResult, mitcRecordsResult] = await Promise.all([
        firestoreService.getCreditCards({}, pageSize + 1),
        mitcService.getMitcRecords({}, pageSize + 1),
      ])
      if (creditCardsResult.data.length > pageSize || mitcRecordsResult.data.length > pageSize) {
        throw new Error(`Card directory exceeds ${pageSize} records per source; the merged list would be incomplete.`)
      }

      const creditCards = creditCardsResult.data
      const mitcAsCards = mitcRecordsResult.data.map(m => this.convertMitcToCard(m))
      const allCards = [...creditCards]

      mitcAsCards.forEach(mitcCard => {
        const existing = creditCards.find(c =>
          c.name.toLowerCase() === mitcCard.name.toLowerCase() &&
          c.bank.toLowerCase() === mitcCard.bank.toLowerCase(),
        )
        if (existing) {
          // Existing card.mitc wins over MITC-record-sourced fields
          existing.mitc = existing.mitc
            ? { ...mitcCard.mitc, ...existing.mitc }
            : mitcCard.mitc
        } else {
          allCards.push(mitcCard)
        }
      })

      // Apply the filter to the merged population. Filtering only the base
      // credit_cards query lets unrelated MITC cards back into the result.
      return allCards.filter(card =>
        (!filters.bank || card.bank === filters.bank) &&
        (!filters.category || card.category === filters.category) &&
        (filters.isActive === undefined || card.isActive === filters.isActive) &&
        (filters.isFeatured === undefined || card.isFeatured === filters.isFeatured))
    } catch (error) {
      logger.error('Error fetching credit cards:', error)
      throw error
    }
  }

  // Convert MITC record to Credit Card format
  private convertMitcToCard(mitcRecord: MitcRecord): CreditCard {
    return {
      id: mitcRecord.id,
      name: mitcRecord.cardName,
      bank: mitcRecord.bankName,
      category: this.inferCategory(mitcRecord.cardName),
      cardType: 'credit' as const,
      description: `${mitcRecord.cardName} from ${mitcRecord.bankName}`,
      features: [],
      eligibility: [],
      imageUrl: mitcRecord.imageUrl || '',
      logoUrl: '',
      annualFee: this.parseNumericValue(mitcRecord.annualFee),
      joiningFee: this.parseNumericValue(mitcRecord.joiningFee),
      interestRate: this.parseNumericValue(mitcRecord.interestRate),
      creditLimit: { min: 10000, max: 500000 },
      rewards: {
        cashbackRate: this.parseNumericValue(mitcRecord.cashbackRate),
        rewardPoints: this.parseNumericValue(mitcRecord.rewardRate),
        welcomeBonus: 0,
        categories: []
      },
      fees: {
        annualFee: this.parseNumericValue(mitcRecord.annualFee),
        joiningFee: this.parseNumericValue(mitcRecord.joiningFee),
        latePaymentFee: this.parseNumericValue(mitcRecord.latePaymentCharges),
        cashAdvanceFee: this.parseCashAdvanceFeePercent(mitcRecord.cashWithdrawalFee),
        foreignTransactionFee: this.parseNumericValue(mitcRecord.foreignTransactionFee)
      },
      mitc: {
        joiningFee: mitcRecord.joiningFee,
        annualFee: mitcRecord.annualFee,
        interestRate: mitcRecord.interestRate,
        latePaymentCharges: mitcRecord.latePaymentCharges,
        foreignTransactionFee: mitcRecord.foreignTransactionFee,
        rewardRate: mitcRecord.rewardRate,
        cashbackRate: mitcRecord.cashbackRate,
        fuelSurchargeWaiver: mitcRecord.fuelSurchargeWaiver,
        interestFreePeriod: mitcRecord.interestFreePeriod,
        addOnCardFee: mitcRecord.addOnCardFee,
        cashWithdrawalFee: mitcRecord.cashWithdrawalFee,
        overLimitFee: mitcRecord.overLimitFee,
        duplicateStatementFee: mitcRecord.duplicateStatementFee,
        cardReplacementFee: mitcRecord.cardReplacementFee,
        emiConversionCharges: mitcRecord.emiConversionCharges,
        gstApplicable: mitcRecord.gstApplicable,
        sourceUrl: mitcRecord.sourceUrl,
        lastUpdated: mitcRecord.updatedAt,
        referralLink: mitcRecord.referralLink
      },
      application: {
        minIncome: 200000,
        minAge: 21,
        maxAge: 65,
        employmentType: ['salaried', 'self-employed'],
        creditScore: { min: 600, max: 900 },
        documents: ['pan', 'aadhar', 'salary-slip'],
        processingTime: '7-10 days',
        approvalRate: 75
      },
      analytics: {
        views: 0,
        applications: 0,
        conversions: 0,
        rating: 0,
        reviewCount: 0
      },
      isActive: true,
      isFeatured: false,
      tags: [mitcRecord.bankName.toLowerCase(), 'mitc'],
      createdAt: mitcRecord.createdAt,
      updatedAt: mitcRecord.updatedAt,
      createdBy: 'mitc-import'
    };
  }

  // Helper to infer category from card name
  private inferCategory(cardName: string): string {
    const name = cardName.toLowerCase();
    if (name.includes('travel') || name.includes('miles') || name.includes('air')) return 'Travel';
    if (name.includes('cash') || name.includes('money')) return 'Cashback';
    if (name.includes('fuel') || name.includes('petrol')) return 'Fuel';
    if (name.includes('shopping') || name.includes('shop')) return 'Shopping';
    if (name.includes('business') || name.includes('corporate')) return 'Business';
    if (name.includes('premium') || name.includes('platinum') || name.includes('signature')) return 'Premium';
    return 'General';
  }

  // Helper to parse numeric values from string
  private parseNumericValue(value: string): number {
    if (!value || value === 'N/A' || value === '-' || value === 'Nil') return 0;
    const numeric = value.replace(/[^\d.]/g, '');
    return parseFloat(numeric) || 0;
  }

  // This numeric projection is a fee percentage. The rupee minimum and other
  // qualifications remain in mitc.cashWithdrawalFee, which the admin UI shows.
  private parseCashAdvanceFeePercent(value: string): number {
    if (!value) return 0;
    for (const match of value.matchAll(/(\d+(?:\.\d+)?)\s*%/g)) {
      const before = value.slice(0, match.index);
      const after = value.slice(match.index + match[0].length);
      // GST is a tax on the fee, not the cash-advance fee rate.
      if (/\bGST(?:\s+at)?\s*$/i.test(before) || /^\s*(?:applicable\s+)?GST\b/i.test(after)) continue;
      const percent = Number(match[1]);
      if (Number.isFinite(percent) && percent >= 0 && percent <= 100) return percent;
    }
    // CreditCard.fees requires a number; zero means no extractable fee rate,
    // not that the full MITC terms describe a free withdrawal.
    return 0;
  }

  // Get a single credit card by ID
  async getCard(cardId: string): Promise<CreditCard | null> {
    try {
      return await firestoreService.getCreditCard(cardId);
    } catch (error) {
      logger.error('Error fetching credit card:', error);
      throw error;
    }
  }

  // Create a new credit card
  async createCard(cardData: CreateCreditCardData): Promise<string> {
    try {
      // Get current authenticated user
      const currentUser = authService.getCurrentUser();
      if (!currentUser) {
        throw new Error('User must be authenticated to create credit cards');
      }

      const cardId = await firestoreService.addCreditCard({
        ...cardData,
        analytics: {
          views: 0,
          applications: 0,
          conversions: 0,
          rating: 0,
          reviewCount: 0
        },
        isActive: true,
        isFeatured: false,
        tags: cardData.tags || [],
        createdBy: currentUser.uid, // Use actual authenticated user ID
        createdAt: new Date(),
        updatedAt: new Date()
      });
      await auditService.writeAudit({
        action:      'create',
        entityType:  'card',
        entityId:    cardId,
        entityLabel: cardLabel({ name: cardData.name, bank: cardData.bank }),
      });
      return cardId;
    } catch (error) {
      logger.error('Error creating credit card:', error);
      throw error;
    }
  }

  // Update an existing credit card
  async updateCard(cardId: string, cardData: Partial<CreditCard>): Promise<void> {
    try {
      const before = await this.getCard(cardId);
      await firestoreService.updateCreditCard(cardId, cardData);
      await auditService.writeAudit({
        action:      'update',
        entityType:  'card',
        entityId:    cardId,
        entityLabel: cardLabel(before),
        changes:     computeDiff(
          (before ?? {}) as unknown as Record<string, unknown>,
          cardData as unknown as Record<string, unknown>,
        ),
      });
    } catch (error) {
      logger.error('Error updating credit card:', error);
      throw error;
    }
  }

  // Delete a credit card
  async deleteCard(cardId: string): Promise<void> {
    try {
      const before = await this.getCard(cardId);
      await firestoreService.deleteCreditCard(cardId);
      await auditService.writeAudit({
        action:      'delete',
        entityType:  'card',
        entityId:    cardId,
        entityLabel: cardLabel(before),
      });
    } catch (error) {
      logger.error('Error deleting credit card:', error);
      throw error;
    }
  }

  // Get card categories
  async getCardCategories(): Promise<string[]> {
    try {
      const cards = await this.getAllCards({}, 1000); // Get all cards to extract categories
      const categories = [...new Set(cards.map(card => card.category))];
      return categories.sort();
    } catch (error) {
      logger.error('Error fetching card categories:', error);
      throw error;
    }
  }

  // Get banks
  async getBanks(): Promise<string[]> {
    try {
      const cards = await this.getAllCards({}, 1000); // Get all cards to extract banks
      const banks = [...new Set(cards.map(card => card.bank))];
      return banks.sort();
    } catch (error) {
      logger.error('Error fetching banks:', error);
      throw error;
    }
  }

  // Toggle card active status
  async toggleCardStatus(cardId: string, isActive: boolean): Promise<void> {
    try {
      await this.updateCard(cardId, { isActive });
    } catch (error) {
      logger.error('Error toggling card status:', error);
      throw error;
    }
  }

  // Toggle card featured status
  async toggleFeaturedStatus(cardId: string, isFeatured: boolean): Promise<void> {
    try {
      await this.updateCard(cardId, { isFeatured });
    } catch (error) {
      logger.error('Error toggling featured status:', error);
      throw error;
    }
  }

  // Bulk update cards
  async bulkUpdateCards(updates: Array<{ id: string; data: Partial<CreditCard> }>): Promise<void> {
    try {
      await firestoreService.batchUpdateCreditCards(updates);
    } catch (error) {
      logger.error('Error bulk updating cards:', error);
      throw error;
    }
  }

  // Get apply link for a card with priority logic
  getApplyLink(card: CreditCard): ApplyLinkResult {
    return ReferralService.getApplyLink(card);
  }

  // Update referral link for a card. Writes to credit_cards.mitc.referralLink
  // if the card exists in credit_cards; falls back to mitc_records.referralLink
  // if it's a MITC-only record. Verifies the write before returning.
  async updateReferralLink(cardId: string, referralUrl: string, notes?: string, addedBy?: string): Promise<void> {
    const linkPayload = {
      url:     referralUrl,
      notes:   notes   || '',
      addedBy: addedBy || '',
      addedAt: new Date(),
    }

    try {
      const card = await this.getCard(cardId)

      if (card) {
        const updateData: UpdateData<CreditCard> = card.mitc
          ? { 'mitc.referralLink': linkPayload }
          : {
              mitc: {
                referralLink:  linkPayload,
                sourceUrl:     '',
                lastUpdated:   new Date(),
              },
            }

        await firestoreService.updateCreditCard(cardId, updateData)

        // Verify the write — protects against silent partial writes
        const updatedCard = await this.getCard(cardId)
        if (!updatedCard?.mitc?.referralLink) {
          throw new Error('Referral link was not saved to database - verification failed')
        }
        if (updatedCard.mitc.referralLink.url !== referralUrl) {
          throw new Error('Referral link URL mismatch in database')
        }
        await auditService.writeAudit({
          action:      'update',
          entityType:  'referral_link',
          entityId:    cardId,
          entityLabel: cardLabel(card),
          changes: {
            url: {
              before: card.mitc?.referralLink?.url ?? null,
              after:  referralUrl,
            },
          },
          metadata: { notes: notes || undefined, source: 'credit_cards' },
        })
        return
      }

      // Not a credit_cards doc — try mitc_records
      const mitcRecord = await mitcService.getMitcRecord(cardId)
      if (mitcRecord) {
        await mitcService.updateMitcRecord(cardId, { referralLink: linkPayload })
        await auditService.writeAudit({
          action:      'update',
          entityType:  'referral_link',
          entityId:    cardId,
          entityLabel: `${mitcRecord.cardName} • ${mitcRecord.bankName}`,
          changes: {
            url: {
              before: mitcRecord.referralLink?.url ?? null,
              after:  referralUrl,
            },
          },
          metadata: { notes: notes || undefined, source: 'mitc_records' },
        })
        return
      }

      throw new Error('Card not found in either credit_cards or mitc_records collections')
    } catch (error) {
      logger.error('Error updating referral link:', error)
      throw error
    }
  }

  // Remove referral link from a card (handles both credit_cards and mitc_records).
  async removeReferralLink(cardId: string): Promise<void> {
    try {
      const card = await this.getCard(cardId)

      if (card && card.mitc) {
        const previousUrl = card.mitc.referralLink?.url ?? null
        await firestoreService.updateCreditCard(cardId, {
          'mitc.referralLink': deleteField(),
        })
        await auditService.writeAudit({
          action:      'delete',
          entityType:  'referral_link',
          entityId:    cardId,
          entityLabel: cardLabel(card),
          changes: { url: { before: previousUrl, after: null } },
          metadata: { source: 'credit_cards' },
        })
        return
      }
      if (card) return // exists but no MITC data — nothing to remove

      const mitcRecord = await mitcService.getMitcRecord(cardId)
      if (mitcRecord) {
        const previousUrl = mitcRecord.referralLink?.url ?? null
        await mitcService.updateMitcRecord(cardId, { referralLink: deleteField() })
        await auditService.writeAudit({
          action:      'delete',
          entityType:  'referral_link',
          entityId:    cardId,
          entityLabel: `${mitcRecord.cardName} • ${mitcRecord.bankName}`,
          changes: { url: { before: previousUrl, after: null } },
          metadata: { source: 'mitc_records' },
        })
        return
      }

      throw new Error('Card not found in either credit_cards or mitc_records collections')
    } catch (error) {
      logger.error('Error removing referral link:', error)
      throw error;
    }
  }

  // Get referral analytics
  async getReferralAnalytics() {
    try {
      const cards = await this.getAllCards();
      return ReferralService.getReferralAnalytics(cards);
    } catch (error) {
      logger.error('Error getting referral analytics:', error);
      throw error;
    }
  }

  // Get cards with admin referral links
  async getCardsWithReferralLinks(): Promise<CreditCard[]> {
    try {
      const cards = await this.getAllCards();
      return ReferralService.getCardsWithReferralLinks(cards);
    } catch (error) {
      logger.error('Error getting cards with referral links:', error);
      throw error;
    }
  }

  // Get cards with only MITC source URLs
  async getCardsWithMitcOnly(): Promise<CreditCard[]> {
    try {
      const cards = await this.getAllCards();
      return ReferralService.getCardsWithMitcOnly(cards);
    } catch (error) {
      logger.error('Error getting cards with MITC only:', error);
      throw error;
    }
  }
}

function cardLabel(card: { name?: string; bank?: string } | null | undefined): string | undefined {
  if (!card) return undefined
  const parts = [card.name, card.bank].filter(Boolean)
  return parts.length ? parts.join(' • ') : undefined
}

export const creditCardService = new CreditCardService();
export default creditCardService;
